/* eslint-disable no-console */
import { INewsItem, getNews } from "~/util/providers/news";
import { getRibbonPoiReviews, listReviews } from "~/util/providers/review";
import { IRibbonMedia, getGalleryRibbon } from "~/util/providers/photo";
import { IPoi } from "~/interfaces";
import { IRibbonData, IRibbonItemPOIData } from "~/ribbon/interfaces";
import { IReviewItem, ILocalityReview } from "~/review/interfaces";
import { _ } from "~/i18n";
import { CONFIG } from "~/config";
import { getIcon } from "./poiagg";
import { LIMITS, PHOTO_SIZE } from "~/ribbon/const";

let reqId = 0;

function setImageDimensions(url: string) {
	return url.replace("{width}", PHOTO_SIZE[0]).replace("{height}", PHOTO_SIZE[1]);
}

function getRibbonMediaUrl(data: IRibbonMedia) {
	const filtered = data.urls.filter(item => item.urlType === "placeholder");

	if (filtered.length === 1) {
		return setImageDimensions(filtered[0].url);
	}

	return "";
}

function getReviewCover(data: IReviewItem) {
	if (data.gallery && data.gallery.length > 0) {
		const firstPhoto = data.gallery[0];

		return setImageDimensions(firstPhoto.urls.default);
	}

	return "";
}

function getReviewDate(data: IReviewItem) {
	return new Intl.DateTimeFormat(CONFIG.defaultLang, { year: "numeric", month: "long", day: "numeric" }).format(new Date(data.updated));
}

function getPoiItem(data: ILocalityReview): IRibbonItemPOIData {
	const imgData = data.description.media?.urls || {
		default: "",
	};
	const poiImage = imgData.default
		? setImageDimensions(imgData.default)
		: "";
	const poiSubtitle = `${data.description.titleVars?.specification} • ${data.description.titleVars?.locationMain1}`;

	return {
		link: `${location.origin}/?source=${data.description.source}&id=${data.description.id}`,
		// poi
		poiBadge: data.badge,
		poiIcon: getIcon(data.description.typeId, { scale: 2 }),
		poiRating: data.review_rating_stars,
		poiRatingText: data.review_rating_caption,
		poiReviewsCount: data.total,
		poiSubtitle,
		poiTitle: data.description.title,
		poiImage,
		poiSource: data.description.source,
		poiId: data.description.id as number,
		poiTypeId: data.description.typeId || 0,
		// hodnoceni
		avatar: "",
		reviewDate: "",
		reviewRating: 0,
		reviewText: "",
		...data.reviews.length > 0
			? {
				avatar: data.reviews[0].user.image_url || "",
				reviewDate: getReviewDate(data.reviews[0] as IReviewItem),
				reviewRating: data.reviews[0].rating,
				reviewText: data.reviews[0].text,
			}
			: {},
	};
}

function convertNewsItems(items: Array<INewsItem>) {
	const news: IRibbonData["news"] = items.map(item => ({
		desc: item.title,
		image: item.cover,
		linkStr: item.source,
		linkUrl: item.url,
		date: item.date,
	}));

	return news;
}

function canBeShown(data: Omit<IRibbonData, "canBeShown">): IRibbonData {
	const counts = [
		data.news.length,
		data.photos.length,
		data.reviews.length,
		data.pois.length,
	];
	const dataContents = counts.filter(item => item > 0).length;

	return {
		...data,
		canBeShown: dataContents >= LIMITS.MIN_CONTENTS,
	};
}

interface IGetDetailData {
	poi: IPoi;
	center: Array<number>;
	bbox: Array<Array<number>>;
	zoom: number;
	geomBbox?: Array<Array<number>>;
}

export async function getDetailData({
	poi,
	center,
	bbox,
	geomBbox,
	zoom,
}: IGetDetailData): Promise<IRibbonData> {
	reqId++;

	const duration: IRibbonData["duration"] = {
		news: 0,
		photos: 0,
		reviews: 0,
		pois: 0,
		total: 0,
	};
	const startTime = Date.now();
	const forRequest = reqId;
	const galleryPhotosLimit = LIMITS.PHOTOS_MATRIX;
	const [newsData, galleryData, reviewsData, poisData] = await Promise.all([
		getNews(poi.title).then(data => {
			duration.news = Date.now() - startTime;

			return data;
		}),
		getGalleryRibbon({
			source: poi.source,
			id: poi.id as number,
			bbox: geomBbox || bbox,
			zoom,
			limit: galleryPhotosLimit,
		}).then(response => {
			duration.photos = Date.now() - startTime;

			return response.data?.data?.gallery || [];
		}, () => null),
		listReviews({
			source: poi.source,
			id: poi.id as number,
			bestReviewsOnly: true,
			filterByLang: true,
			limit: LIMITS.LOAD_REVIEWS,
		}).then(response => {
			duration.reviews = Date.now() - startTime;

			return response.reviews || [];
		}, () => []),
		getRibbonPoiReviews({
			lon: center[0],
			lat: center[1],
			zoom,
			bbox: geomBbox
				? [geomBbox[0][0], geomBbox[0][1], geomBbox[1][0], geomBbox[1][1]]
				: null,
			source: poi.source,
			id: poi.id as number,
		}).then(response => {
			duration.pois = Date.now() - startTime;

			return response.pois;
		}, () => []),
	]);

	if (forRequest !== reqId) {
		console.log(`Not same request id ${forRequest} vs ${reqId}`);

		return null;
	}

	duration.total = Date.now() - startTime;

	const news = convertNewsItems(newsData.slice(0, LIMITS.NEWS));
	const photos: IRibbonData["photos"] = galleryData.slice(0, galleryPhotosLimit).map((item: IRibbonMedia) => ({
		link: `${location.origin}/?source=${poi.source}&id=${poi.id}&gallery=1&sourcep=${item.source}&idp=${item.id}`,
		imageSrc: getRibbonMediaUrl(item),
		imageWidth: item.width,
		imageHeight: item.height,
		source: item.source,
		id: item.id,
	}));
	const reviews: IRibbonData["reviews"] = (reviewsData as Array<IReviewItem>).slice(0, LIMITS.REVIEWS).map(item => ({
		link: `${location.origin}/?source=${poi.source}&id=${poi.id}&recenze=1`,
		avatar: item.user?.image_url || "",
		userName: item.user?.name || "",
		reviewsCount: _("detail.content.review2.reviewCount", { count: item.user.review_count }),
		rating: item.rating,
		text: item.text,
		date: getReviewDate(item),
		image: getReviewCover(item),
		source: poi.source,
		id: poi.id as number,
	}));
	const pois: IRibbonData["pois"] = (poisData as Array<ILocalityReview>).filter(item => !(item.source === poi.source && item.id === poi.id) && item.description).slice(0, LIMITS.POIS).map(item => getPoiItem(item));

	return canBeShown({
		news,
		photos,
		reviews,
		pois,
		duration,
	});
}
